// File for translations

export function getLangFromPath(pathname) {
  return pathname.startsWith('/en') ? 'en' : 'gr';
}

export function stripLangPrefix(pathname) {
  if (pathname.startsWith('/en')) {
    return pathname.replace('/en', '') || '/';
  }
  return pathname;
}

export function buildLocalizedPath(path, lang) {
  const cleanPath = stripLangPrefix(path);

  if (lang === 'en') {
    return cleanPath === '/' ? '/en' : `/en${cleanPath}`;
  }

  return cleanPath;
}

export const content = {
  en: {
    header: {
      home: 'Home',
      contact: 'Contact',
      enterApp: 'Open the App'
    },
    footer: {
      copy: 'A simple and direct way to access Green Your Freight services.',
      contact: 'Contact',
      usefulLinks: 'Useful Links',
      followUs: 'Follow Us',
      businessInfo: 'Business Info',
      officialSite: 'Official Project Website',
      openApp: 'Open Web App',
      support: 'Customer Support',
      copyright: '© 2026 Green Your Freight Service',
      tagline: 'Fast, reliable and more sustainable urban deliveries.',
      vat: 'VAT Number',
      taxOffice: 'Tax Office',
      gemi: 'GEMI',
      address: 'Address',
      actualAddress: 'Acharnon 335, Athens / Athens Central, 11145'
    },
    home: {
      eyebrow: 'Smarter last-mile delivery',
      heroTitle1: 'Fast, reliable and',
      heroTitle2: ' more sustainable deliveries',
      heroText:
        'Green Your Freight helps you move goods across the city in a smarter way — whether you are sending a single package or managing deliveries for your business.',
      enterApp: 'Open the App',
      officialSite: 'Visit Official Project Site',

      introMini: 'Built around the service',
      introTitle: 'Quick, Easy, Sustainable',
      introText:
        'Designed to make things simple: understand how the service works, access the platform quickly and get the support you need without digging through pages.',

      aboutLabel: 'About the service',
      aboutTitle: 'What Green Your Freight actually does',
      aboutLead:
        'Green Your Freight connects senders and carriers to make last-mile deliveries more efficient, flexible and environmentally friendly.',
      aboutBody:
        'Whether you are an individual sending a package, a business offering delivery options at checkout, or a driver handling routes, the platform brings everyone into one shared system that organizes requests, matches deliveries and keeps the process transparent.',

      benefits: [
        'Flexible delivery options for individuals and businesses.',
        'Better use of vehicles through shared logistics.',
        'Reduced environmental impact in urban transport.',
        'A single platform that connects shippers and carriers in real time.'
      ],

      infoCardTitle: 'Logistics, without the friction',
      infoCardText:
        'Instead of fixed delivery chains, requests are shared with available carriers who can accept and complete them. This creates a more dynamic system that adapts to real demand and availability.',
      launchPlatform: 'Open the platform',

      whyLabel: 'Why it works',
      whyTitle: 'Simple flow, real-world use',
      whyText:
        'The idea is straightforward: make it easy to send, easy to accept and easy to track. No unnecessary steps, no confusion.',

      feature1Title: 'Quick access',
      feature1Text: 'Create or accept a delivery request in just a few steps.',
      feature2Title: 'Clear process',
      feature2Text: 'From request to delivery, everything is visible and structured.',
      feature3Title: 'Real support',
      feature3Text: 'You can always reach out if something is unclear or goes wrong.',

      howLabel: 'How it works',
      howTitle: 'From request to delivery',
      howText:
        'A delivery request is created, shared with available carriers and picked up by someone ready to handle it. From there, the process moves forward with clear time windows and updates.',

      steps: [
        'Create a delivery request through the platform.',
        'A carrier accepts the request based on availability.',
        'Pickup and delivery are completed within agreed time windows.'
      ],

      openWebApp: 'Open Web App',
      downloadApp: 'Download App',

      ctaMini: 'Ready to send something?',
      ctaTitle: 'Get started with Green Your Freight',
      ctaText: 'Use the platform to create, manage or deliver shipments in a more flexible way.',
      contactSupport: 'Contact Support'
    },
    contact: {
      label: 'Contact & support',
      title: 'We are here if you need us',
      intro:
        'If you have questions about the service, need help with a delivery or want to explore a partnership, send us a message and we will get back to you.',
      formTitle: 'Send us a message',
      fullName: 'Full name',
      email: 'Email address',
      subject: 'Subject',
      message: 'How can we help?',
      send: 'Send Message',
      actualAddress: 'Acharnon 335, Athens / Athens Central, 11145'
    },
    cookies: {
      consentModal: {
        title: 'Manage consent',
        description:
          'We use cookies to keep the website running properly and, if you agree, to understand how it is used so we can improve it. You can accept all cookies, reject non-essential ones or adjust your preferences.',
        acceptAllBtn: 'Accept all',
        acceptNecessaryBtn: 'Reject non-essential',
        showPreferencesBtn: 'Manage preferences',
        footer:
          '<a href="/privacy-policy">Privacy Policy</a> <a href="/cookie-policy">Cookie Policy</a>'
      },
      preferencesModal: {
        title: 'Cookie preferences',
        acceptAllBtn: 'Accept all',
        acceptNecessaryBtn: 'Reject non-essential',
        savePreferencesBtn: 'Save preferences',
        closeIconLabel: 'Close modal',
        sections: [
          {
            title: 'How we use cookies',
            description:
              'Some cookies are required for the website to work, while others help us understand usage and improve the experience.'
          },
          {
            title: 'Strictly necessary cookies',
            description:
              'These cookies are essential for the website and cannot be disabled.',
            linkedCategory: 'necessary'
          },
          {
            title: 'Analytics cookies',
            description:
              'These cookies help us understand traffic and usage. They are currently inactive until analytics is connected.',
            linkedCategory: 'analytics'
          },
          {
            title: 'More information',
            description:
              'For more details, please check our Privacy Policy and Cookie Policy.'
          }
        ]
      },
      settingsButton: 'Cookie settings'
    }
  },

  gr: {
    header: {
      home: 'Αρχική',
      contact: 'Επικοινωνία',
      enterApp: 'Άνοιγμα Εφαρμογής'
    },
    footer: {
      copy: 'Ένας απλός και άμεσος τρόπος πρόσβασης στις υπηρεσίες του Green Your Freight.',
      contact: 'Επικοινωνία',
      usefulLinks: 'Χρήσιμοι Σύνδεσμοι',
      followUs: 'Ακολουθήστε μας',
      businessInfo: 'Στοιχεία Επιχείρησης',
      officialSite: 'Επίσημη Ιστοσελίδα Έργου',
      openApp: 'Άνοιγμα Web App',
      support: 'Υποστήριξη Πελατών',
      copyright: '© 2026 Green Your Freight Service',
      tagline: 'Γρήγορες, αξιόπιστες και πιο βιώσιμες αστικές παραδόσεις.',
      vat: 'ΑΦΜ',
      taxOffice: 'ΔΟΥ',
      gemi: 'ΓΕΜΗ',
      address: 'Διεύθυνση',
      actualAddress: 'ΑΧΑΡΝΩΝ 335, ΑΘΗΝΑΙΩΝ / ΚΕΝΤΡΙΚΟΥ ΤΟΜΕΑ ΑΘΗΝΩΝ, 11145'
    },
    home: {
      eyebrow: 'Πιο έξυπνες last-mile παραδόσεις',
      heroTitle1: 'Γρήγορες, αξιόπιστες και',
      heroTitle2: ' πιο βιώσιμες παραδόσεις',
      heroText:
        'Το Green Your Freight σας βοηθά να μεταφέρετε δέματα μέσα στην πόλη με πιο έξυπνο τρόπο — είτε στέλνετε ένα πακέτο είτε διαχειρίζεστε παραδόσεις για την επιχείρησή σας.',
      enterApp: 'Άνοιγμα Εφαρμογής',
      officialSite: 'Επίσκεψη στην Επίσημη Ιστοσελίδα',

      introMini: 'Εστιασμένο στην υπηρεσία',
      introTitle: 'Γρήγορο, Εύκολο, Βιώσιμο',
      introText:
        'Σχεδιασμένη για απλότητα: καταλάβετε πώς λειτουργεί η υπηρεσία, μπείτε γρήγορα στην πλατφόρμα και βρείτε υποστήριξη χωρίς περιττή αναζήτηση.',

      aboutLabel: 'Σχετικά με την υπηρεσία',
      aboutTitle: 'Τι κάνει στην πράξη το Green Your Freight',
      aboutLead:
        'Το Green Your Freight συνδέει αποστολείς και μεταφορείς για πιο αποδοτικές, ευέλικτες και φιλικές προς το περιβάλλον last-mile παραδόσεις.',
      aboutBody:
        'Είτε είστε ιδιώτης, είτε επιχείρηση που προσφέρει επιλογές μεταφοράς, είτε οδηγός που εκτελεί παραδόσεις, η πλατφόρμα φέρνει όλους σε ένα κοινό σύστημα που οργανώνει αιτήματα, τα αντιστοιχίζει και διατηρεί τη διαδικασία ξεκάθαρη.',

      benefits: [
        'Ευέλικτες επιλογές παράδοσης για ιδιώτες και επιχειρήσεις.',
        'Καλύτερη αξιοποίηση οχημάτων μέσω κοινής διαχείρισης.',
        'Μειωμένο περιβαλλοντικό αποτύπωμα στις αστικές μεταφορές.',
        'Μία πλατφόρμα που συνδέει αποστολείς και μεταφορείς σε πραγματικό χρόνο.'
      ],

      infoCardTitle: 'Logistics χωρίς περιττή τριβή',
      infoCardText:
        'Αντί για σταθερές αλυσίδες μεταφοράς, τα αιτήματα μοιράζονται σε διαθέσιμους μεταφορείς που μπορούν να τα αναλάβουν. Έτσι δημιουργείται ένα πιο ευέλικτο σύστημα που προσαρμόζεται στη ζήτηση.',
      launchPlatform: 'Άνοιγμα πλατφόρμας',

      whyLabel: 'Γιατί λειτουργεί',
      whyTitle: 'Απλή ροή, πραγματική χρήση',
      whyText:
        'Η λογική είναι απλή: εύκολη αποστολή, εύκολη αποδοχή και ξεκάθαρη παρακολούθηση. Χωρίς περιττά βήματα.',

      feature1Title: 'Γρήγορη πρόσβαση',
      feature1Text: 'Δημιουργήστε ή αποδεχτείτε ένα αίτημα σε λίγα βήματα.',
      feature2Title: 'Καθαρή διαδικασία',
      feature2Text: 'Από το αίτημα μέχρι την παράδοση, όλα είναι ορατά και οργανωμένα.',
      feature3Title: 'Πραγματική υποστήριξη',
      feature3Text: 'Υπάρχει πάντα τρόπος επικοινωνίας αν χρειαστείτε βοήθεια.',

      howLabel: 'Πώς λειτουργεί',
      howTitle: 'Από το αίτημα στην παράδοση',
      howText:
        'Δημιουργείται ένα αίτημα, κοινοποιείται σε διαθέσιμους μεταφορείς και αναλαμβάνεται από κάποιον που μπορεί να το εκτελέσει. Στη συνέχεια ορίζονται χρονικά παράθυρα και η διαδικασία προχωρά με ενημερώσεις.',

      steps: [
        'Δημιουργήστε ένα αίτημα παράδοσης μέσω της πλατφόρμας.',
        'Ένας μεταφορέας το αποδέχεται ανάλογα με τη διαθεσιμότητα.',
        'Η παραλαβή και η παράδοση ολοκληρώνονται εντός των συμφωνημένων χρονικών πλαισίων.'
      ],

      openWebApp: 'Άνοιγμα Web App',
      downloadApp: 'Κατεβάστε την Εφαρμογή',

      ctaMini: 'Έχετε κάτι να στείλετε;',
      ctaTitle: 'Ξεκινήστε με το Green Your Freight',
      ctaText: 'Χρησιμοποιήστε την πλατφόρμα για να δημιουργήσετε, να διαχειριστείτε ή να εκτελέσετε αποστολές με μεγαλύτερη ευελιξία.',
      contactSupport: 'Επικοινωνία με Υποστήριξη'
    },
    contact: {
      label: 'Επικοινωνία & υποστήριξη',
      title: 'Είμαστε εδώ αν μας χρειαστείτε',
      intro:
        'Αν έχετε απορίες για την υπηρεσία, χρειάζεστε βοήθεια με κάποια παράδοση ή θέλετε να συζητήσουμε συνεργασία, στείλτε μας μήνυμα και θα σας απαντήσουμε.',
      formTitle: 'Στείλτε μας μήνυμα',
      fullName: 'Ονοματεπώνυμο',
      email: 'Διεύθυνση email',
      subject: 'Θέμα',
      message: 'Πώς μπορούμε να βοηθήσουμε;',
      send: 'Αποστολή Μηνύματος',
      actualAddress: 'ΑΧΑΡΝΩΝ 335, ΑΘΗΝΑΙΩΝ / ΚΕΝΤΡΙΚΟΥ ΤΟΜΕΑ ΑΘΗΝΩΝ, 11145'
    },
    cookies: {
      consentModal: {
        title: 'Διαχείριση συγκατάθεσης',
        description:
          'Χρησιμοποιούμε cookies για τη σωστή λειτουργία της ιστοσελίδας και, αν συμφωνείτε, για να κατανοούμε τη χρήση της και να τη βελτιώνουμε. Μπορείτε να αποδεχτείτε όλα ή να επιλέξετε τι προτιμάτε.',
        acceptAllBtn: 'Αποδοχή όλων',
        acceptNecessaryBtn: 'Απόρριψη μη απαραίτητων',
        showPreferencesBtn: 'Διαχείριση προτιμήσεων',
        footer:
          '<a href="/gr/privacy-policy">Πολιτική Απορρήτου</a> <a href="/gr/cookie-policy">Πολιτική Cookies</a>'
      },
      preferencesModal: {
        title: 'Προτιμήσεις cookies',
        acceptAllBtn: 'Αποδοχή όλων',
        acceptNecessaryBtn: 'Απόρριψη μη απαραίτητων',
        savePreferencesBtn: 'Αποθήκευση προτιμήσεων',
        closeIconLabel: 'Κλείσιμο',
        sections: [
          {
            title: 'Πώς χρησιμοποιούμε τα cookies',
            description:
              'Κάποια cookies είναι απαραίτητα για τη λειτουργία της ιστοσελίδας, ενώ άλλα μας βοηθούν να τη βελτιώνουμε.'
          },
          {
            title: 'Απαραίτητα cookies',
            description:
              'Αυτά τα cookies είναι αναγκαία και δεν μπορούν να απενεργοποιηθούν.',
            linkedCategory: 'necessary'
          },
          {
            title: 'Cookies ανάλυσης',
            description:
              'Βοηθούν στην κατανόηση της χρήσης της ιστοσελίδας. Προς το παρόν είναι ανενεργά.',
            linkedCategory: 'analytics'
          },
          {
            title: 'Περισσότερες πληροφορίες',
            description:
              'Δείτε την Πολιτική Απορρήτου και την Πολιτική Cookies για περισσότερα.'
          }
        ]
      },
      settingsButton: 'Ρυθμίσεις cookies'
    }
  }
};